import * as React from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import { FormattedDateTime, DateTimeDisplayStyle } from '../../components/DataDisplay/FormattedDateTime';
import { MomentConfig } from '../../components/DataDisplay/MomentConfig';
import { MatterDate } from './TimelineInfoStream';
import { MatterTerminology } from '../../MatterTerminology';
import moment from 'moment';

export enum IconSelector {
    Overdue = 0,
    DueComing = 1,
    Today = 2,
    Completed = 3
}

const STATUS_TREATMENT: { [key in IconSelector]: { icon: React.ComponentType<{ fontSize?: 'inherit' | 'small' }>; color: string; bg: string } } = {
    [IconSelector.Overdue]: { icon: ReportProblemOutlinedIcon, color: 'var(--rv-accent-red)', bg: 'var(--rv-accent-red-bg)' },
    [IconSelector.DueComing]: { icon: ScheduleOutlinedIcon, color: 'var(--rv-accent-grey)', bg: 'var(--rv-accent-grey-bg)' },
    [IconSelector.Today]: { icon: EventOutlinedIcon, color: 'var(--rv-accent-orange)', bg: 'var(--rv-accent-orange-bg)' },
    [IconSelector.Completed]: { icon: CheckCircleOutlinedIcon, color: 'var(--rv-accent-green)', bg: 'var(--rv-accent-green-bg)' },
};

// Explicit request: not a plain data table - a real vertical timeline (connecting rail + a
// status dot per entry), matching what this tab is actually named/for. The rail is this
// design's one deliberate structural device; everything hung off it (title, type, dates,
// status text) stays plain so the rail itself stays the focal point.
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            display: 'flex',
            alignItems: 'stretch',
        },
        rail: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: 40,
            flexShrink: 0,
        },
        dot: {
            width: 32,
            height: 32,
            minHeight: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': {
                fontSize: 17,
            },
        },
        railLine: {
            flex: 1,
            width: 2,
            minHeight: theme.spacing(2),
            backgroundColor: theme.palette.divider,
            margin: theme.spacing(0.5, 0),
        },
        content: {
            flex: 1,
            minWidth: 0,
            paddingBottom: theme.spacing(2.5),
            paddingLeft: theme.spacing(1.5),
        },
        // Explicit request: keep the status badge right next to the title instead of pushed to
        // the far edge of the content column (justify-content:space-between reads as a huge
        // gap on a wide screen) - title, type chip, and status badge all sit together, close
        // to the rail, and wrap together on narrow screens too.
        contentHeader: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: theme.spacing(0.75),
            minWidth: 0,
        },
        title: {
            fontSize: 14,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.35,
            wordBreak: 'break-word',
        },
        typeChip: {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
        },
        statusBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '3px 9px',
            borderRadius: 6,
            fontSize: 11.5,
            fontWeight: 700,
        },
        meta: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: theme.spacing(0.75),
            marginTop: 4,
            fontSize: 12,
            color: theme.palette.text.secondary,
        },
        metaSeparator: {
            color: theme.palette.divider,
        },
        // FormattedDateTime's own root element is a <div> (block), so nesting it straight
        // after a label pushes the value onto its own line - flattening it back to inline
        // keeps "Completed: 12 Jan 2020 (6 years ago)" on one line instead of wrapping the
        // date under its own label.
        metaLabel: {
            display: 'inline-flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: '0.25em',
            '& > div': {
                display: 'inline',
            },
        },
    })
);

export interface TimelineInfoCardProps {
    data: MatterDate;
    isLast?: boolean;
}

// tslint:disable-next-line:max-line-length
export function GetIconForDate(isCompleted: boolean, dueDate?: Date, completedDate?: Date): { iconSource: IconSelector; displayDate?: Date, stringDisplay: string } {
    var dateToDisplay = isCompleted ? completedDate : dueDate;

    const now = moment().startOf('day');

    if (isCompleted) {
        return {
            iconSource: IconSelector.Completed,
            displayDate: dateToDisplay,
            stringDisplay: MatterTerminology.Completed
        };
    } else if (moment(dateToDisplay).diff(now, 'days') > 0) {
        return {
            iconSource: IconSelector.DueComing,
            displayDate: dateToDisplay,
            stringDisplay: MatterTerminology.Due
        };
    } else if (moment(dateToDisplay).diff(now, 'days') === 0) {
        return {
            iconSource: IconSelector.Today,
            displayDate: dateToDisplay,
            stringDisplay: MatterTerminology.Today
        };
    } else {
        return {
            iconSource: IconSelector.Overdue,
            displayDate: dateToDisplay,
            stringDisplay: MatterTerminology.Overdue
        };
    }
}

function convertDate(value: Date): Date | undefined {
    if (value) {
        return new Date(Date.parse(value.toString()));
    }
    return undefined;
}

export const RvTimelineInfoCard: React.FC<TimelineInfoCardProps> = (props) => {
    const classes = useStyles();

    var completed = props.data.completed;
    var completedDate = convertDate(props.data.completedDate);
    var dueDate = convertDate(props.data.dueDate);

    const iconInfo = GetIconForDate(completed, dueDate, completedDate);
    const { icon: StatusIcon, color, bg } = STATUS_TREATMENT[iconInfo.iconSource];
    // Same config the original RvDateTimeDisplay usage passed - ShowDateWithMoment adds the
    // "(x years ago)" relative-time suffix for anything not yet completed, ShowOnlyDate omits
    // it once something's done.
    const dateConfig = completed ? MomentConfig.ShowOnlyDate : MomentConfig.ShowDateWithMoment;
    const hasType = !!props.data.type && props.data.type.trim().toLowerCase() !== 'none';

    return (
        <div className={classes.row}>
            <div className={classes.rail}>
                <span className={classes.dot} style={{ backgroundColor: bg, color }}>
                    <StatusIcon fontSize="small" />
                </span>
                {!props.isLast && <span className={classes.railLine} />}
            </div>
            <div className={classes.content}>
                <div className={classes.contentHeader}>
                    <span className={classes.title}>{props.data.description}</span>
                    <span className={classes.statusBadge} style={{ backgroundColor: bg, color }}>
                        {iconInfo.stringDisplay}
                    </span>
                </div>
                <div className={classes.meta}>
                    {/* Explicit request: prefix with "Type:" so it's clear what the chip is
                        rather than a bare unlabelled value. Some tenants don't set a type at
                        all and the field comes back as the literal string "None" rather than
                        empty/null - treated the same as no type, so no "Type: None" chip shows. */}
                    {hasType && <span className={classes.typeChip}>{MatterTerminology.Type}: {props.data.type}</span>}
                    {hasType && (completedDate || dueDate) && <span className={classes.metaSeparator}>&middot;</span>}
                    {completedDate && (
                        <span className={classes.metaLabel}>
                            {MatterTerminology.Completed}: <FormattedDateTime value={iconInfo.displayDate} displayStyle={DateTimeDisplayStyle.ShortDate} config={dateConfig} DisplayInUTC={false} />
                        </span>
                    )}
                    {completedDate && dueDate && <span className={classes.metaSeparator}>&middot;</span>}
                    {dueDate && (
                        <span className={classes.metaLabel}>
                            {MatterTerminology.Due}: <FormattedDateTime value={iconInfo.displayDate} displayStyle={DateTimeDisplayStyle.ShortDate} config={dateConfig} DisplayInUTC={false} />
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};
